import { apiUrl } from "../utils/api";
import type { VollmachtDto, VollmachtVorschauDaten } from "../components/vollmachten/vollmachtTypes";

/**
 * Der Server kennt die Vollmachten-Routen (noch) nicht — etwa auf localhost,
 * solange DEV das neue Backend nicht hat. Nest antwortet dann mit 404
 * „Cannot POST /api/…"; das unterscheidet ihn von einem fachlichen 404.
 */
export class VollmachtFunktionFehlt extends Error {
  constructor() {
    super("Diese Funktion ist auf dem Server noch nicht verfügbar (Vorschau folgt nach dem Deploy).");
    this.name = "VollmachtFunktionFehlt";
  }
}

export class VollmachtFehler extends Error {
  constructor(public readonly status: number, message: string) {
    super(message);
    this.name = "VollmachtFehler";
  }
}

async function pruefe(res: Response): Promise<Response> {
  if (res.ok) return res;
  let meldung = "";
  try {
    const body = await res.clone().json();
    meldung = Array.isArray(body?.message) ? body.message.join(", ") : String(body?.message ?? "");
  } catch {
    meldung = await res.text().catch(() => "");
  }
  if (res.status === 405 || (res.status === 404 && /^Cannot (GET|POST|DELETE|PUT|PATCH) /.test(meldung))) {
    throw new VollmachtFunktionFehlt();
  }
  throw new VollmachtFehler(res.status, meldung || `Anfrage fehlgeschlagen (${res.status}).`);
}

async function rufe(path: string, init?: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(apiUrl(path), init);
  } catch {
    throw new VollmachtFehler(0, "Keine Verbindung zum Server.");
  }
  return pruefe(res);
}

const json = (body: unknown): RequestInit => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** Blob als Datei speichern (ohne pdfjs — siehe Memory „useOpenDocument zieht pdfjs mit"). */
export function blobHerunterladen(blob: Blob, dateiname: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = dateiname;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export const vollmachtService = {
  /** `{ enabled:false }` auch dann, wenn der Server die Route nicht kennt. */
  async signaturStatus(): Promise<{ enabled: boolean; verfuegbar: boolean }> {
    try {
      const res = await rufe("vollmachten/signatur/status");
      const body = await res.json();
      return { enabled: !!body?.enabled, verfuegbar: true };
    } catch (err) {
      if (err instanceof VollmachtFunktionFehlt) return { enabled: false, verfuegbar: false };
      return { enabled: false, verfuegbar: true };
    }
  },

  async vorschau(art: string, daten: VollmachtVorschauDaten): Promise<Blob> {
    const res = await rufe("vollmachten/vorschau", json({ art, ...daten }));
    return res.blob();
  },

  async liste(contactId: number): Promise<VollmachtDto[]> {
    return (await rufe(`contact/${contactId}/vollmachten`)).json();
  },

  async anlegen(contactId: number, arten: string[]): Promise<VollmachtDto[]> {
    return (await rufe(`contact/${contactId}/vollmachten`, json({ arten }))).json();
  },

  async pdf(contactId: number, vid: number): Promise<Blob> {
    return (await rufe(`contact/${contactId}/vollmachten/${vid}/pdf`)).blob();
  },

  async verknuepfen(contactId: number, vid: number, documentId: number): Promise<VollmachtDto> {
    return (await rufe(`contact/${contactId}/vollmachten/${vid}/dokument`, json({ documentId }))).json();
  },

  async signierlink(contactId: number, vid: number): Promise<{ url: string; expiresAt: string }> {
    return (await rufe(`contact/${contactId}/vollmachten/${vid}/signierlink`, { method: "POST" })).json();
  },

  async widerrufen(contactId: number, vid: number): Promise<VollmachtDto> {
    return (await rufe(`contact/${contactId}/vollmachten/${vid}/signierlink`, { method: "DELETE" })).json();
  },

  async entfernen(contactId: number, vid: number): Promise<void> {
    await rufe(`contact/${contactId}/vollmachten/${vid}`, { method: "DELETE" });
  },

  // ── öffentlich (ohne Login) ──
  async oeffentlich(token: string): Promise<
    | { titel: string; status: "erledigt" }
    | { titel: string; status: "offen"; kontaktName: string; maklerName: string; ablauf: string; digitalMoeglich: boolean }
  > {
    return (await rufe(`public/vollmacht/${encodeURIComponent(token)}`)).json();
  },

  async oeffentlichesPdf(token: string): Promise<Blob> {
    return (await rufe(`public/vollmacht/${encodeURIComponent(token)}/pdf`)).blob();
  },

  async unterschreiben(token: string): Promise<{ signierUrl: string }> {
    return (await rufe(`public/vollmacht/${encodeURIComponent(token)}/unterschreiben`, { method: "POST" })).json();
  },
};
